import { Box, Button, Typography } from "@mui/material";
import { useQuery } from "react-query";
import StatsCard from "src/components/common/StatsCard";
import defaultStyles, { StylesClasses } from "../styles";
import { getStyles } from "src/styles/theme";
import {
  getLandDashboardDetails,
  getTableDashboardDetails,
} from "src/utils/api/dashboard";
import {
  NewSelectedDashBoardTable,
  NewSelectedTableProps,
} from "src/components/common/DashBoardTable/newSelectedDashboard";
import {
  exportNewSelectedTableToXlsx,
  formatDataForBarChart,
} from "src/utils/helper";
import DashboardChartSkeleton from "src/components/common/DashboardChartSkeleton";
import { useEffect, useMemo, useState } from "react";
import MultiSelect from "src/components/common/MultiSelect";
import { getStates } from "src/utils/api/roles";
import StatusBarChart from "src/components/common/StatusBarChart";
import { AxiosResponse } from "axios";
import { usePersistedFilters } from "src/hooks/usePersistedFilters";

const TOP_HEADERS = [
  "",
  "REGISTERED UNDER NEW OM",
  "APPROVED UNDER NEW OM",
  "ALL INCL. DESELECT",
  "NEW SELECTED (ACTIVE)",
  "PROVISIONAL SELECTED",
  "SITE VISITED",
  "ACKNOWLEDGE BY APPROVER",
  "DESELECT",
  "TOTAL AVAILABLE LAND",
];

const SUB_HEADERS = [
  "S.No",
  "State",
  ...Array.from({ length: TOP_HEADERS.length - 1 }).flatMap(() => [
    "No",
    "Area (ha)",
  ]),
];

const NewSelectedLand = () => {
  const [isTabLoading, setIsTabLoading] = useState(true);
  const [states, setStates] = useState<any>([]);
  const [tableRows, setTableRows] = useState<any[]>([]);
  const [filtersState, setFiltersState] = useState([]);

  const { persisted, saveFilters } = usePersistedFilters("mw_newSelected");

  const [queryParams, setQueryParams] = useState<{
    projectStateIds?: string[];
    type: string;
  }>({
    projectStateIds: persisted?.queryParams?.projectStateIds ?? undefined,
    type: "newlySelected",
  });

  const [filterKey, setFilterKey] = useState(
    persisted?.filterKey ?? { projectStateIds: null }
  );

  const { data: dashboard, isLoading: dashboardLoading } = useQuery(
    ["newSelectedDashboard", queryParams],
    () => getLandDashboardDetails(queryParams),
    {
      onSuccess: () => {},
      onError: () => {},
      refetchOnMount: true,
    }
  );

  const { isLoading } = useQuery(
    ["table-newlySelected", queryParams],
    () => getTableDashboardDetails(queryParams),
    {
      onSuccess: (res: AxiosResponse) => {
        setTableRows(
          res?.data?.stats.map((item: any, idx: number) => ({
            id: idx,
            sno: idx + 1,
            state: item.state,
            totalNumberProvisionalSelected: item.totalNumberProvisionalSelected,
            totalAreaProvisionalSelected: item.totalAreaProvisionalSelected,
            totalNumberSiteVisited: item.totalNumberSiteVisited,
            totalAreaSiteVisited: item.totalAreaSiteVisited,
            totalNumberAcknowledgeByApprover:
              item.totalNumberAcknowledgeByApprover,
            totalAreaAcknowledgeByApprover: item.totalAreaAcknowledgeByApprover,
            totalNumberDeselect: item.totalNumberDeselect,
            totalAreaDeselect: item.totalAreaDeselect,
            totalNumberNewSelected: item.totalNumberNewSelected,
            totalAreaNewSelected: item.totalAreaNewSelected,
            totalNumberAllIncludingDeselect:
              item.totalNumberAllIncludingDeselect,
            totalAreaAllIncludingDeselect: item.totalAreaAllIncludingDeselect,
            totalNumberRegisteredNewOm: item.totalNumberRegisteredNewOm,
            totalAreaRegisteredNewOm: item.totalAreaRegisteredNewOm,
            totalNumberApprovedNewOm: item.totalNumberApprovedNewOm,
            totalAreaApprovedNewOm: item.totalAreaApprovedNewOm,
            totalNumberAvailableLand: item.totalNumberAvailableLand,
            totalAreaAvailableLand: item.totalAreaAvailableLand,
          }))
        );
        setIsTabLoading(false);
      },
      onError: () => {},
      refetchOnMount: true,
    }
  );

  useQuery(["getStatesType"], () => getStates(), {
    onSuccess: (data) => {
      setStates(data.data);
    },
    refetchOnMount: true,
  });

  const formattedData = useMemo(
    () => formatDataForBarChart(dashboard?.data?.stats),
    [dashboard?.data?.stats]
  );

  const routesConfig: Record<string, string> = {
    All: "/",
    "ACKNOWLEDGE_BY_APPROVER,SITE_VISITED,PROVISIONAL_SELECTED":
      "/?currentTab=ACKNOWLEDGE_BY_APPROVER,SITE_VISITED,PROVISIONAL_SELECTED",
    PROVISIONAL_SELECTED: "/?currentTab=PROVISIONAL_SELECTED",
    SITE_VISITED: "/?currentTab=SITE_VISITED",
    ACKNOWLEDGE_BY_APPROVER: "/?currentTab=ACKNOWLEDGE_BY_APPROVER",
    DESELECT: "/?currentTab=DESELECT",
  };

  const styles = getStyles<StylesClasses>(defaultStyles);

  const dashboardTableData: NewSelectedTableProps = {
    topHeader: TOP_HEADERS,
    header: SUB_HEADERS,
    data: tableRows,
  };

  useEffect(() => {
    setFiltersState(dashboard?.data?.state);
  }, [dashboard]);

  useEffect(() => {
    saveFilters({ queryParams, filterKey });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, filterKey]);

  useEffect(() => {
    setIsTabLoading(true);
  }, []);

  return (
    <>
      <Box {...styles("topHeader")}>
        <Box
          sx={{
            marginBottom: "5px",
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {filtersState && (
              <Box
                sx={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                Selected States:-
                {filtersState?.map((filterState, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      height: "46px",
                      padding: "11px",
                      width: "fit-content",
                      border: "1px solid grey",
                      borderRadius: "24px",
                      backgroundColor: "#eee",
                    }}
                  >
                    {filterState}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: "8px" }}>
            <MultiSelect
              options={states}
              value={filterKey.projectStateIds}
              onChange={(e: any) => {
                setFilterKey((prevFilter) => {
                  const newState = {
                    ...prevFilter,
                    projectStateIds: e.length ? e : null,
                  };
                  setQueryParams({
                    ...queryParams,
                    projectStateIds: newState?.projectStateIds,
                  });
                  return newState;
                });
              }}
            />
          </Box>
        </Box>

        {!dashboardLoading && (
          <Box
            sx={{ width: "100%", marginBottom: "20px", alignSelf: "center" }}
          >
            <StatusBarChart
              data={formattedData}
              label={"Status of New Selected land Blocks"}
            />
          </Box>
        )}

        <Box {...styles("statsCardWrapper")}>
          {dashboard?.data?.stats?.map((stat: any, index: number) => (
            <StatsCard
              key={index}
              title={`${stat?.description?.toUpperCase()}`}
              value={`${stat?.number}`}
              area={stat?.totalArea}
              customStyles={{ valueCard: styles("statsCard").sx }}
              link={routesConfig[stat?.type]}
            />
          ))}
        </Box>
      </Box>

      {isLoading || isTabLoading ? (
        <DashboardChartSkeleton />
      ) : (
        <Box {...styles("dashboardTableWrapper")}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="h5" {...styles("heading")}>
              Status of New Selected Land Under GCP
            </Typography>
            <Button
              variant="outlined"
              onClick={() =>
                exportNewSelectedTableToXlsx(
                  dashboardTableData.data,
                  dashboardTableData.header,
                  dashboardTableData.topHeader
                )
              }
            >
              Export
            </Button>
          </Box>
          <NewSelectedDashBoardTable {...dashboardTableData} />
        </Box>
      )}
    </>
  );
};

export default NewSelectedLand;
